/**
 * Embedded-presentation state for the Moderation Console.
 *
 * Two things put the console into its embedded presentation (no universal
 * header): being rendered inside a frame, or being handed an explicit signal.
 *
 * The signal exists because a new top-level window is never framed. An
 * operator working under Portal's header who follows an internal link that
 * opens a new tab would otherwise land on the legacy universal header
 * mid-task, because frame detection correctly reports "not framed". The state
 * has to be carried across that boundary rather than inferred.
 *
 * Absent or unrecognised signal means legacy behaviour — the header renders.
 * That default is deliberately the opposite of the frame check below, which
 * fails closed to embedded: a cross-origin frame we cannot inspect is still a
 * frame, whereas a query parameter we do not recognise is not a promise of
 * anything.
 */

export const EMBED_PARAM = 'vf_embed';
export const EMBED_VALUE = '1';

/**
 * True when this document is rendered inside a frame.
 *
 * Fails closed to embedded: if the comparison throws, we are in a frame whose
 * parent we are not allowed to inspect, which is still a frame.
 *
 * @returns {boolean} whether this document is framed
 */
export const isFramed = () => {
    try {
        return window.self !== window.top;
    }
    catch {
        return true;
    }
};

/**
 * True only for the exact expected signal. Any other value — a missing
 * parameter, an empty one, "0", "true", or the parameter repeated at all —
 * is treated as absent, and so as legacy behaviour.
 *
 * @param {string} [search] query string to read, defaulting to the live one
 * @returns {boolean} whether the embed signal is present and well formed
 */
export const hasEmbedSignal = (search = window.location.search) => {
    // getAll, not get: a repeated parameter is ambiguous in either order, and
    // ambiguous is not a promise of anything, so it falls back to legacy.
    const values = new URLSearchParams(search).getAll(EMBED_PARAM);

    return values.length === 1 && values[0] === EMBED_VALUE;
};

/**
 * True when the console should render in its embedded presentation.
 *
 * @param {string} [search] query string to read, defaulting to the live one
 * @returns {boolean} whether this is an embedded session
 */
export const isEmbeddedSession = (search) => isFramed() || hasEmbedSignal(search);

let captured;

/**
 * The embedded state as it was when the app first asked, captured once.
 *
 * The console is a history-mode SPA, so an in-app route change rewrites the
 * url and drops the signal from window.location.search. Re-reading the live
 * query string on every link would therefore work for the first new tab and
 * silently stop working after the operator navigated once inside it. The
 * state belongs to the session, not to the current route.
 *
 * @returns {boolean} whether this session is embedded
 */
export const embeddedSession = () => {
    if (captured === undefined) {
        captured = isEmbeddedSession();
    }
    return captured;
};

/** Clears the captured state. Test seam. */
export const resetEmbeddedSession = () => {
    captured = undefined;
};

/**
 * Append the embed signal to an INTERNAL url.
 *
 * Only ever call this for links that stay inside the Viafoura admin estate.
 * Outbound links (publisher pages, uploaded media, and links inside
 * user-authored comment text) must never carry it, and the Portal link must
 * not carry it either — it is the deliberate crossing point out of the legacy
 * presentation and into Portal's own.
 *
 * @param {string} url internal url to append the signal to
 * @param {boolean} [embedded] override for the current session state
 * @returns {string} the url, with the signal appended when embedded
 */
export const withEmbedSignal = (url, embedded = embeddedSession()) => {
    if (!embedded) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${EMBED_PARAM}=${EMBED_VALUE}`;
};
